// Libs
import classNames from 'classnames/bind';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { Loading } from '~/components/common';
import { ItemOrderSchedule } from '~/components/module/order';
// Others
import { IRangeDate } from '~/utils/interface/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getListOrderEvent } from '~/thunks/order/orderThunk';
import { IOrderEventList, IQueryParamOrderEvent } from '~/utils/interface/order';
import { orderActions, selectIsRefreshOrderEventList } from '~/thunks/order/orderSlice';
// Styles, images, icons
import styles from './AvailableOrders.module.scss';

type Props = {
  isDragging: boolean;
  rangeDate: IRangeDate;
  isView?: boolean;
  onStartDrag?: (item: IOrderEventList) => void;
};

const cx = classNames.bind(styles);

const AvailableOrders = (props: Props) => {
  //#region Destructuring Props
  const { rangeDate, isDragging, isView = false, onStartDrag } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const isRefreshListOrderEvent = useSelector(selectIsRefreshOrderEventList);
  //#endregion Selector

  //#region Declare State
  const [listOrder, setListOrder] = useState<IOrderEventList[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [params, setParams] = useState<IQueryParamOrderEvent>({
    startDate: rangeDate.startDate,
    endDate: rangeDate.endDate,
  });
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setParams({
      ...params,
      startDate: rangeDate.startDate,
      endDate: rangeDate.endDate,
    });
  }, [rangeDate]);

  useEffect(() => {
    handleGetListOrder(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!isRefreshListOrderEvent) return;

    handleGetListOrder(params);

    dispatch(orderActions.setRefreshOrderEventList(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshListOrderEvent]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListOrder = (params: IQueryParamOrderEvent) => {
    setIsLoading(true);

    dispatch(getListOrderEvent(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const responses = res?.data as IOrderEventList[];

        setListOrder(responses);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <Box id='availableOrdersComponent' className={cx('container')}>
      <Stack direction={'row'} spacing={'6px'}>
        <Typography className={cx('title')}>{t('order_item_title_available_orders')}</Typography>
        <Box className={cx('numberOrders')}>{isView ? listOrder?.length : 0}</Box>
      </Stack>

      <Stack spacing={3} className={cx('listOrder', { dragging: isDragging })}>
        {isView && listOrder.length > 0 ? (
          listOrder?.map((item, index) => (
            <ItemOrderSchedule key={index} item={item} onStartDrag={() => onStartDrag?.(item)} />
          ))
        ) : (
          <Stack flex={1} justifyContent={'center'}>
            <Typography component={'p'} className={cx('noData')}>
              {t('common_no_data_available', { ns: 'common/base' })}
            </Typography>
          </Stack>
        )}
      </Stack>

      {isLoading && <Loading />}
    </Box>
  );
};

export default AvailableOrders;
